import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { startPlanCheckout, SERVICE_MENU } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { avisoPagosPronto, pagosNoDisponibles } from "@/lib/pagos";
import CustomPlanCard from "./CustomPlanCard";

const PLANS = [
  { id: "essential", index: "01", cta: "SELECT ESSENTIAL PLAN", featured: false, lookup: "essential_project" },
  { id: "strategic", index: "02", cta: "SUBSCRIBE TO STRATEGIC", featured: true, lookup: "strategic_monthly" },
  { id: "enterprise", index: "03", cta: "REQUEST ENGAGEMENT", featured: false, modal: true },
];

const cardAnim = (i) => ({
  initial: { opacity: 0, y: 36 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.15 },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: i * 0.08 },
});

export default function PricingSection({ onEnterprise }) {
  const { t } = useLang();
  const [selected, setSelected] = useState([]);
  const [loadingPlan, setLoadingPlan] = useState(null);

  const toggleService = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));

  const startPlan = async (plan) => {
    setLoadingPlan(plan.id);
    try {
      const { data } = await startPlanCheckout(plan.lookup);
      if (data.checkout_url) window.location.href = data.checkout_url;
      else toast.error(t.custom.toasts.error);
    } catch (e) {
      if (pagosNoDisponibles(e)) avisoPagosPronto(t, onEnterprise);
      else toast.error(t.custom.toasts.error);
    } finally {
      setLoadingPlan(null);
    }
  };

  return (
    <section id="plans" className="relative bg-[#F8FAFC] py-24 lg:py-32">
      <div className="max-w-[1320px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6 }}
              className="eyebrow text-[#C5B358] flex items-center gap-3"
            >
              <span className="w-2 h-2 rotate-45 bg-[#C5B358]" /> {t.pricing.overline}
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7, delay: 0.08 }}
              className="font-display mt-5 text-4xl sm:text-5xl lg:text-[56px] leading-[1.05] font-bold text-[#0F1E3D] max-w-2xl"
            >
              {t.pricing.titleA}
              <em className="font-serif-accent text-[#C5B358]">{t.pricing.titleEm}</em>
              {t.pricing.titleB}
            </motion.h2>
          </div>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="text-[#64748B] max-w-sm leading-relaxed"
          >
            {t.pricing.sub}
          </motion.p>
        </div>

        <motion.div
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          className="origin-left h-[2px] bg-[#C5B358] mb-12"
          data-testid="pricing-gold-sweep"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 items-stretch">
          {PLANS.map((plan, i) => {
            const p = t.pricing.plans[plan.id];
            return (
              <motion.div
                key={plan.id}
                {...cardAnim(i)}
                data-testid={`pricing-col-${plan.id}`}
                className={`relative bg-white rounded-xl p-8 flex flex-col transition-all duration-300 hover:-translate-y-1 ${
                  plan.featured
                    ? "border-2 border-[#C5B358] shadow-[0_12px_24px_rgba(15,30,61,0.08)]"
                    : "border border-[#E2E8F0] hover:shadow-[0_12px_24px_rgba(15,30,61,0.08)]"
                }`}
              >
                {p.badge && (
                  <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-[#C5B358] text-[#0F1E3D] eyebrow px-4 py-1.5 rounded-full whitespace-nowrap">
                    {p.badge}
                  </span>
                )}
                <div className="eyebrow text-[#C5B358]">
                  {plan.index} / {p.kicker}
                </div>
                <h3 className="mt-3 text-lg font-bold tracking-[0.04em] uppercase text-[#0F1E3D]">
                  ATLANTIS {plan.id.toUpperCase()}
                </h3>

                <div className="mt-5">
                  <span className="font-display text-[28px] font-bold tabular-nums text-[#0F1E3D]">
                    {p.price}
                  </span>
                  {p.unit && <span className="text-base text-[#64748B]"> {p.unit}</span>}
                  <div className="text-[13px] text-[#64748B] mt-1.5 leading-snug">{p.subtitle}</div>
                </div>

                <p className="text-[13px] font-medium text-[#1E293B] border-t border-[#E2E8F0] mt-4 pt-4">
                  {p.desc}
                </p>

                <ul className="my-5 space-y-3 flex-grow">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5">
                      <Check size={15} strokeWidth={3} className="text-[#C5B358] flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-[#1E293B] leading-snug">{f}</span>
                    </li>
                  ))}
                </ul>

                <button
                  data-testid={`btn-${plan.id}-plan`}
                  onClick={() => (plan.modal ? onEnterprise() : startPlan(plan))}
                  disabled={loadingPlan === plan.id}
                  className={`mt-auto w-full rounded-md py-3.5 text-[12px] font-bold uppercase tracking-[0.1em] transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-60 ${
                    plan.featured
                      ? "bg-[#C5B358] hover:bg-[#B3A146] text-[#0F1E3D]"
                      : "bg-[#0F1E3D] hover:bg-[#1a2e5c] text-white"
                  }`}
                >
                  {loadingPlan === plan.id && <Loader2 size={14} className="animate-spin" />}
                  {plan.cta}
                </button>
              </motion.div>
            );
          })}

          <CustomPlanCard selected={selected} onToggle={toggleService} onContact={onEnterprise} />
        </div>
        <p className="mt-10 text-center text-[12px] text-[#64748B] max-w-3xl mx-auto leading-relaxed" data-testid="pricing-legal">
          {t.pricing.legal}
        </p>
      </div>
    </section>
  );
}